import { useMemo, useState } from 'react'
import { Download } from 'lucide-react'
import { cn } from '@/lib/utils'
import { InfoPopover } from '@/components/InfoPopover'
import { MONO, eur, eurS, pct } from './format'
import { Segmented } from './Segmented'
import { Dropdown } from './Dropdown'
import { TrendBars } from './TrendBars'
import { BreakdownChart, type BreakdownView } from './BreakdownChart'
import { MoversList } from './MoversList'
import { EditorialMovers } from './EditorialMovers'
import { RankList } from './RankList'
import { DrillModal, type DrillContent } from './DrillModal'
import { agg, computeMovers, deltaPct, paymentList, priorWindow, scaleMonths, spanLabel, windowMonths } from './aggregate'
import { LEVELS, LEVEL_NOUN, LEVEL_TERM, UNRESOLVED, hasHierarchy, levelSeries, levelTotal, levelValues } from './levels'
import type { BreakdownDim, CashCollectionsProps, MoverFilter, RangeKey, RankItem } from '../types'

const BVIEWS: readonly BreakdownView[] = ['By month', 'vs Prior']
const MOVER_FILTERS: readonly MoverFilter[] = ['All', 'Declines', 'Gains']

function Pill({ k, v, neg }: { k: string; v: string; neg?: boolean }) {
  return (
    <div className="rounded-xl border border-stone-200 bg-stone-50/50 px-3 py-2 dark:border-stone-800 dark:bg-stone-800/30">
      <p className="text-[11px] text-stone-400">{k}</p>
      <p className={cn('mt-0.5 text-sm font-semibold', MONO, neg && 'text-red-600 dark:text-red-400')}>{v}</p>
    </div>
  )
}
function WfRow({ k, v, cls }: { k: string; v: string; cls?: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-stone-500 dark:text-stone-400">{k}</span>
      <span className={cn('font-medium', MONO, cls)}>{v}</span>
    </div>
  )
}
function LeakRow({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-stone-500 dark:text-stone-400">{k}</span>
      <span className={cn('font-medium text-red-600 dark:text-red-400', MONO)}>{v}</span>
    </div>
  )
}

/** Cash Collections — multi-month range view with prior-period comparison. */
export function CashCollections({ data, onDrilldown, onExport }: CashCollectionsProps) {
  const [entityKey, setEntityKey] = useState(data.entities[0].key)
  const [range, setRange] = useState<RangeKey>(data.defaultRange)
  const [endKey, setEndKey] = useState(data.defaultEndMonth)
  const [dim, setDim] = useState<BreakdownDim>('Department')
  const [bView, setBView] = useState<BreakdownView>('vs Prior')
  const [moverFilter, setMoverFilter] = useState<MoverFilter>('All')
  const [drill, setDrill] = useState<DrillContent | null>(null)

  const entity = data.entities.find((e) => e.key === entityKey) ?? data.entities[0]
  const scaled = useMemo(() => scaleMonths(data.months, entity.share), [data.months, entity.share])
  const win = useMemo(() => windowMonths(scaled, endKey, range), [scaled, endKey, range])
  const prior = useMemo(() => priorWindow(scaled, win), [scaled, win])
  const cur = useMemo(() => agg(win), [win])
  const prev = useMemo(() => agg(prior), [prior])

  // Breakdown level (schema v2): every series at the chosen level, then the ones
  // with cash in the window (By month) or in either window (vs Prior). Each level
  // sums to net sales; Category adds the department-level (unresolved) amounts.
  const hierarchy = hasHierarchy(data)
  const series = useMemo(() => levelSeries(data, dim), [data, dim])
  const curV = useMemo(() => levelValues(cur, dim), [cur, dim])
  const prevV = useMemo(() => levelValues(prev, dim), [prev, dim])
  const shownSeries = series.filter((s) => curV[s.key] || (bView === 'vs Prior' && prevV[s.key]))
  const hiddenCount = series.filter((s) => !s.unresolved).length - shownSeries.filter((s) => !s.unresolved).length
  const unresolvedShown = shownSeries.filter((s) => s.unresolved)
  const levelSum = levelTotal(curV)
  const rounding = Math.round(levelSum - cur.netSales)
  const levelNoun = LEVEL_NOUN[dim]
  const levelPlural = dim === 'Category' ? 'categories' : `${levelNoun}s`
  const movers = useMemo(() => computeMovers(cur, prev, dim, series), [cur, prev, dim, series])
  const declines = movers.filter((m) => m.dir === 'decline').length
  const gains = movers.length - declines
  // Curated movers from the source dashboard take precedence over computed ones.
  const editorialMovers = data.movers
  const edDeclines = editorialMovers ? editorialMovers.filter((m) => m.dir === 'decline').length : declines
  const edGains = editorialMovers ? editorialMovers.length - edDeclines : gains

  const payments = paymentList(cur, data.paymentMethods)
  const currSpan = spanLabel(win)
  const priorSpan = spanLabel(prior)
  const hasCmp = prev.count > 0
  const dNet = cur.netCash - prev.netCash
  const dPct = deltaPct(cur.netCash, prev.netCash)
  const avg = cur.count ? cur.netCash / cur.count : 0

  // Top revenue / products carry the LATEST month's leaders (the payload's snapshot),
  // shown at true value — scale only by the entity share. Do NOT multiply by cur.count/6:
  // that was a design-mock hangover (its sample list held 6-month totals) and it divided
  // our real single-month figures by up to 6 — e.g. the €267,611 clinic monthly-fee showed
  // as €44,602 at the 1M view.
  const lscale = entity.share
  const topMonthLabel = data.months.length ? data.months[data.months.length - 1].label : ''
  const clinics: RankItem[] = data.topClinics.list.map((i) => ({ ...i, eur: Math.round(i.eur * lscale) }))
  const products: RankItem[] = data.topProducts.list.map((i) => ({ ...i, eur: Math.round(i.eur * lscale) }))
  const refundList: RankItem[] = data.leakage.largest.map((i) => ({ ...i, sub: i.prod, eur: Math.round(i.eur * entity.share) }))
  const clinicsTop = clinics.reduce((t, c) => t + c.eur, 0)
  const productsCore = products.reduce((t, p) => t + p.eur, 0)
  const leakTotal = cur.refunds + cur.fees + cur.fx

  const monthOpts = data.months.map((m) => ({ value: m.key, label: m.label }))
  const entityOpts = data.entities.map((e) => ({ value: e.key, label: e.label }))

  // The drill keeps today's lines (curated detail when the payload has it, else
  // the collected amount); a category adds its glossary entry, an unresolved
  // amount says why it sits at department level.
  const drillMeta = (key: string) => {
    const s = series.find((x) => x.key === key)
    const label = s?.label ?? key
    const unresolved = key.startsWith(UNRESOLVED)
    const det = data.moverDetails[key]
    const why =
      det?.why ??
      (unresolved
        ? `AR rows from ${s?.group ?? 'this department'} whose product text matched no category, so the pipeline could only place them at department level. They stay in the Category view so it still sums to net sales.`
        : undefined)
    const term = dim === 'Category' && !unresolved ? `category:${key}` : undefined
    return { label, det, why, term }
  }
  const openBreakdown = (key: string, monthLabel: string) => {
    const mo = win.find((m) => m.label === monthLabel)
    const value = levelValues(mo, dim)[key] || 0
    const { label, det, why, term } = drillMeta(key)
    setDrill({
      tag: dim,
      title: label,
      sub: `${monthLabel} · cash collected`,
      total: value,
      why,
      term,
      lines: det ? det.lines.map((l) => ({ name: l.name, curr: l.curr })) : [{ name: `${label} collected`, curr: value }],
    })
    onDrilldown?.(dim, key, monthLabel)
  }
  const openCategory = (key: string) => {
    const cv = curV[key] || 0
    const pv = prevV[key] || 0
    const { label, det, why, term } = drillMeta(key)
    setDrill({
      tag: `${dim} · ${currSpan} vs ${priorSpan}`,
      title: label,
      sub: 'current period vs prior period',
      total: cv,
      why,
      term,
      lines: det ? det.lines.map((l) => ({ name: l.name, prev: l.prev, curr: l.curr })) : [{ name: label, prev: pv, curr: cv }],
    })
    onDrilldown?.(dim, key)
  }
  return (
    <div className="space-y-5">
      {/* Head */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Cash Collections</h1>
        <p className="mt-1 max-w-3xl text-sm text-stone-500 dark:text-stone-400">
          Where the money came in and what it cost to collect, across {currSpan}
          {hasCmp ? `, compared with the prior ${cur.count}-month period (${priorSpan})` : ''}. Customer inflows only,
          net of refunds, fees and FX.
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <Dropdown label="Entity" value={entityKey} options={entityOpts} onChange={setEntityKey} />
        <div className="flex items-center gap-1.5 rounded-lg border border-champagne-300/50 bg-champagne-50/40 px-2.5 py-1 dark:border-champagne-500/20 dark:bg-champagne-500/5">
          <span className="text-[10px] font-medium uppercase tracking-wider text-champagne-700 dark:text-champagne-300">Range</span>
          <Segmented options={data.ranges} value={range} onChange={setRange} size="sm" ariaLabel="Range length" />
        </div>
        <Dropdown label="Period ending" value={endKey} options={monthOpts} onChange={setEndKey} />
        <span className="ml-auto hidden text-[11px] text-stone-400 sm:inline">{data.fxNote}</span>
        <button
          onClick={() => onExport?.()}
          className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-white px-3 py-1.5 text-sm font-medium text-stone-600 hover:bg-stone-50 dark:border-stone-700 dark:bg-stone-900 dark:text-stone-300 dark:hover:bg-stone-800"
        >
          <Download className="h-4 w-4" />
          Export
        </button>
      </div>

      {/* Top row: hero + right stack */}
      <div className="grid gap-4 lg:grid-cols-12">
        <div className="flex flex-col rounded-2xl border border-stone-200 bg-white p-5 sm:p-6 lg:col-span-7 dark:border-stone-800 dark:bg-stone-900">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-stone-500 dark:text-stone-400">Net cash collected · {range}</p>
            <span className="text-[11px] text-stone-400">{cur.count} {cur.count === 1 ? 'month' : 'months'} · {currSpan}</span>
          </div>
          <p className={cn('mt-3 text-4xl font-semibold tracking-tight sm:text-5xl', MONO)}>{eur(cur.netCash)}</p>
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-stone-50 px-2.5 py-1 text-[11px] text-stone-500 dark:bg-stone-800/50">
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: '#2C7D52' }} /> Reconciles to bank payouts
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Pill k="Net sales" v={eur(cur.netSales)} />
            <Pill k="Gross inflows" v={eur(cur.gross)} />
            <Pill k="Cost to collect" v={'−' + eur(cur.cost)} neg />
            <Pill k="Avg / month" v={eur(avg)} />
          </div>
          <div className="mt-5 flex flex-1 flex-col">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-stone-600 dark:text-stone-300">By Payment Method</span>
              <span className="text-[11px] text-stone-400">{currSpan}</span>
            </div>
            <div className="mt-2 flex flex-1 flex-col justify-around gap-2">
              {payments.map((p) => (
                <div key={p.key} className="flex items-center gap-3">
                  <span className="w-20 shrink-0 text-sm text-stone-700 sm:w-28 dark:text-stone-200">{p.label}</span>
                  <span className="hidden text-[10px] uppercase tracking-wider text-stone-400 sm:inline">{p.code}</span>
                  <div className="ml-auto flex items-center gap-3">
                    <div className="h-1.5 w-16 overflow-hidden rounded bg-stone-100 sm:w-40 dark:bg-stone-800">
                      <div className="h-full rounded" style={{ width: `${pct(p.eur, cur.gross)}%`, backgroundColor: '#AE9056' }} />
                    </div>
                    <span className={cn('w-24 text-right text-sm font-medium', MONO)}>{eur(p.eur)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-4 lg:col-span-5">
          {/* waterfall */}
          <div className="rounded-2xl border border-stone-200 bg-white p-5 dark:border-stone-800 dark:bg-stone-900">
            <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 dark:text-stone-400">Cost to collect · {currSpan}</p>
            <p className={cn('mt-1 text-2xl font-semibold text-emerald-700 dark:text-emerald-400', MONO)}>{eur(cur.netCash)}</p>
            <div className="mt-3 space-y-1.5 text-sm">
              <WfRow k="Gross inflows" v={eur(cur.gross)} cls="text-emerald-600 dark:text-emerald-400" />
              <WfRow k="Refunds" v={'−' + eur(cur.refunds)} cls="text-red-600 dark:text-red-400" />
              <WfRow k="Transaction fees" v={'−' + eur(cur.fees)} cls="text-red-600 dark:text-red-400" />
              <WfRow k="FX losses" v={'−' + eur(cur.fx)} cls="text-red-600 dark:text-red-400" />
              <div className="flex items-center justify-between border-t border-stone-100 pt-1.5 dark:border-stone-800">
                <span className="text-sm font-medium">Net cash collected</span>
                <span className={cn('text-sm font-semibold', MONO)}>{eur(cur.netCash)}</span>
              </div>
            </div>
            <p className="mt-2 text-[11px] text-stone-400">fees {pct(cur.fees, cur.gross).toFixed(2)}% of gross · refunds {pct(cur.refunds, cur.gross).toFixed(2)}% of gross</p>
          </div>

          {/* delta card */}
          {hasCmp && (
            <div className="rounded-2xl border border-stone-200 bg-white p-5 dark:border-stone-800 dark:bg-stone-900">
              <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 dark:text-stone-400">vs prior {cur.count}M · net cash</p>
              <p className={cn('mt-1 text-3xl font-semibold', MONO, dNet >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')}>
                {eurS(dNet)}
              </p>
              <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
                {currSpan} {eur(cur.netCash)} vs {priorSpan} {eur(prev.netCash)}
              </p>
              <span
                className={cn(
                  'mt-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold',
                  dNet >= 0 ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300' : 'bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-300',
                )}
              >
                {dNet >= 0 ? '▲' : '▼'} {Math.abs(dPct)}% vs prior {cur.count}-month period
              </span>
            </div>
          )}

        </div>
      </div>

      {/* Net cash trend — only when the range spans more than one month */}
      {cur.count > 1 && (
        <div className="rounded-2xl border border-stone-200 bg-white p-5 dark:border-stone-800 dark:bg-stone-900">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 dark:text-stone-400">Net cash · {cur.count}-month trend</p>
            <span className="text-[11px] text-stone-400">{currSpan}</span>
          </div>
          <div className="mt-4">
            <TrendBars months={win} />
          </div>
        </div>
      )}

      {/* Movers — curated editorial list when provided, else computed at the breakdown level */}
      <div className="rounded-2xl border border-stone-200 bg-white p-5 dark:border-stone-800 dark:bg-stone-900">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="text-sm font-semibold text-stone-800 dark:text-stone-100">Where the money moved</h3>
          <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[11px] font-medium text-stone-500 dark:bg-stone-800 dark:text-stone-400">
            {editorialMovers ? editorialMovers.length : movers.length}
          </span>
          <span className="hidden text-xs text-stone-400 sm:inline">
            {edDeclines} declines · {edGains} gains · ranked by € value · {currSpan} vs {priorSpan}
            {editorialMovers ? '' : ' · tap a row to expand'}
          </span>
          <div className="ml-auto">
            <Segmented options={MOVER_FILTERS} value={moverFilter} onChange={setMoverFilter} size="sm" ariaLabel="Mover filter" />
          </div>
        </div>
        <div className="mt-3">
          {editorialMovers ? (
            <EditorialMovers movers={editorialMovers} filter={moverFilter} priorLabel={priorSpan} currLabel={currSpan} />
          ) : (
            <MoversList movers={movers} filter={moverFilter} details={data.moverDetails} priorSpan={priorSpan} currSpan={currSpan} />
          )}
        </div>
      </div>

      {/* Breakdown — Business unit | Department | Category */}
      <div className="rounded-2xl border border-stone-200 bg-white p-5 dark:border-stone-800 dark:bg-stone-900">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-stone-800 dark:text-stone-100">
              {bView === 'vs Prior' ? `${currSpan} vs ${priorSpan} · where the difference comes from` : `${currSpan} · breakdown by month`}
            </h3>
            <p className="mt-0.5 text-xs text-stone-400">
              {bView === 'vs Prior'
                ? `Each ${levelNoun} this period vs the prior ${cur.count}-month period — green grew, red declined`
                : `Each bar is one month, split by ${levelNoun}`}{' '}
              · hover for detail, click to open transactions
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1">
              <Segmented options={LEVELS} value={dim} onChange={setDim} size="sm" ariaLabel="Breakdown level" />
              <InfoPopover term={LEVEL_TERM[dim]} />
            </span>
            <Segmented options={BVIEWS} value={bView} onChange={setBView} size="sm" ariaLabel="Breakdown view" />
          </div>
        </div>
        {hierarchy ? (
          <>
            <div className="mt-5">
              <BreakdownChart
                view={bView}
                months={win}
                cur={cur}
                prev={prev}
                dim={dim}
                series={shownSeries}
                currSpan={currSpan}
                priorSpan={priorSpan}
                onDrill={(k, mo) => (mo ? openBreakdown(k, mo) : openCategory(k))}
              />
            </div>
            <p className="mt-4 border-t border-stone-100 pt-3 text-[11px] leading-relaxed text-stone-400 dark:border-stone-800 dark:text-stone-500">
              Showing the <span className="font-medium text-stone-600 dark:text-stone-300">{dim}</span> level · {currSpan}: {dim} total{' '}
              <span className={cn('text-stone-600 dark:text-stone-300', MONO)}>{eurS(levelSum)}</span> · net sales{' '}
              <span className={cn('text-stone-600 dark:text-stone-300', MONO)}>{eurS(cur.netSales)}</span>
              {rounding !== 0 ? ` (${eurS(rounding)} integer rounding)` : ''}. Business unit, Department and Category each sum to net sales.
              {hiddenCount > 0 ? ` ${hiddenCount} ${hiddenCount === 1 ? levelNoun : levelPlural} with no cash ${bView === 'vs Prior' ? 'in either period' : 'in this period'} not drawn.` : ''}
              {unresolvedShown.length > 0
                ? ` Unresolved (${unresolvedShown
                    .map((s) => `${s.group} ${eurS(curV[s.key] || 0)}${curV[s.key] ? '' : `, prior ${eurS(prevV[s.key] || 0)}`}`)
                    .join('; ')}) = AR rows the pipeline could only place at department level; kept so Category still sums to net sales.`
                : ''}
            </p>
          </>
        ) : (
          <p className="mt-4 text-sm text-stone-500 dark:text-stone-400">
            This data bundle predates the Business unit › Department › Category view. Upload a refresh built with the current export (schema v2) to see it.
          </p>
        )}
      </div>

      {/* Bottom row — stacks on mobile, three across from sm up */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-stone-200 bg-white p-5 dark:border-stone-800 dark:bg-stone-900">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-stone-800 dark:text-stone-100">Top revenue</h3>
            <span className="text-[11px] text-stone-400">By clinic · {topMonthLabel}</span>
          </div>
          <div className="mb-3 mt-2 flex items-end justify-between">
            <div>
              <p className="text-[11px] text-stone-400">Top {clinics.length} clinics</p>
              <p className={cn('text-lg font-semibold', MONO)}>{eur(clinicsTop)}</p>
            </div>
            <div className="text-right">
              <p className="text-[11px] text-stone-400">Share of net sales</p>
              <p className={cn('text-lg font-semibold text-emerald-600 dark:text-emerald-400', MONO)}>{data.topClinics.share}%</p>
            </div>
          </div>
          <RankList items={clinics} />
        </div>

        <div className="rounded-2xl border border-stone-200 bg-white p-5 dark:border-stone-800 dark:bg-stone-900">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-stone-800 dark:text-stone-100">Top products</h3>
            <span className="text-[11px] text-stone-400">By revenue · {topMonthLabel}</span>
          </div>
          <div className="mb-3 mt-2 flex items-end justify-between">
            <div>
              <p className="text-[11px] text-stone-400">Recurring core</p>
              <p className={cn('text-lg font-semibold', MONO)}>{eur(productsCore)}</p>
            </div>
            <div className="text-right">
              <p className="text-[11px] text-stone-400">Clinic fees + subs</p>
              <p className={cn('text-lg font-semibold text-emerald-600 dark:text-emerald-400', MONO)}>{data.topProducts.sharePct}%</p>
            </div>
          </div>
          <RankList items={products} />
        </div>

        <div className="rounded-2xl border border-stone-200 bg-white p-5 dark:border-stone-800 dark:bg-stone-900">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-stone-800 dark:text-stone-100">Refunds &amp; leakage</h3>
            <span className="text-[11px] text-stone-400">Cost to collect · {range}</span>
          </div>
          <div className="mb-3 mt-2 flex items-end justify-between">
            <div>
              <p className="text-[11px] text-stone-400">Total leakage</p>
              <p className={cn('text-lg font-semibold', MONO)}>{eur(leakTotal)}</p>
            </div>
            <div className="text-right">
              <p className="text-[11px] text-stone-400">Refund items</p>
              <p className={cn('text-lg font-semibold', MONO)}>{data.leakage.refundCount}</p>
            </div>
          </div>
          <p className="mb-2 text-[11px] font-medium uppercase tracking-wider text-stone-400">
            All refunds · {topMonthLabel} ({refundList.length})
          </p>
          <div className="max-h-72 overflow-y-auto pr-1">
            <RankList items={refundList} signed />
          </div>
          <div className="mt-4 space-y-1.5 border-t border-stone-100 pt-3 dark:border-stone-800">
            <LeakRow k={`Transaction fees (${pct(cur.fees, cur.gross).toFixed(2)}%)`} v={'−' + eur(cur.fees)} />
            <LeakRow k="FX losses" v={'−' + eur(cur.fx)} />
            <LeakRow k="Refunds total" v={'−' + eur(cur.refunds)} />
          </div>
        </div>
      </div>

      <p className="text-[11px] leading-relaxed text-stone-400 dark:text-stone-500">
        Figures are customer cash inflows by disbursement date, net of refunds, fees and FX, aggregated over the
        selected window and compared with the prior equal-length period. Top revenue, top products and refunds are
        the latest month's exact figures from the AR cash sheet (2026 Cash Analysis · AR - DATA); mover explanations
        are curated.
      </p>

      <DrillModal content={drill} onClose={() => setDrill(null)} />
    </div>
  )
}
